import axios from 'axios';
import { resolvePreRegisterConfig } from './preRegisterConfig';
import { buildCAPayload, buildCAFailResponse } from './caPayload';

export const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * ผลลัพธ์ default ของ response ที่ตอบ 200: ถ้า data.data[0].fail > 0 ถือว่ารายการนี้ไม่สำเร็จ
 * (รูปแบบของ ThaID v7 — response ของ endpoint อื่นที่ data.data ไม่ใช่ array จะผ่านเป็นสำเร็จเหมือนเดิม)
 * config ตัวไหนมี response แบบอื่นให้ใส่ config.parseResult(data) => { ok, response } override ได้
 */
export function parseResult(config, data) {
  if (config.parseResult) return config.parseResult(data);
  const result = data?.data?.[0];
  if (result && Number(result.fail) > 0) {
    return { ok: false, response: result.failDetail?.[0]?.response ?? data };
  }
  return { ok: true, response: data };
}

/**
 * ยิง 1 รายการตาม config ที่ resolve จาก objective — ใช้ร่วมกันทั้งรอบแรก (AddMember) และ retry
 * คืน { ok, response, is_duplicate? } เสมอ ไม่ throw
 *  - ประกอบ payload ใน try: TypeError จาก field ว่าง (เช่น name_th เป็น null) นับเป็น fail ของรายการนั้น
 *  - HTTP 204 = fail (business rule เดิม) แต่ไม่ค้างอีกต่อไป
 *  - response.message ที่อยู่ใน config.okMessages (เช่น 'This user is already in the business.') = สำเร็จ + is_duplicate
 */
export async function submitPreRegisterItem(request, ctx) {
  const config = resolvePreRegisterConfig(ctx.objective, ctx.objectiveCheck);
  try {
    const { data, status } = await axios({
      method: 'POST',
      url: config.endpoint,
      data: config.buildPayload(request, ctx),
    });
    if (status === 204) {
      return { ok: false, response: { message: 'API returned 204 No Content', status } };
    }
    return parseResult(config, data);
  } catch (err) {
    const response = err?.response?.data ?? { message: err?.message || 'Unknown error' };
    if (config.okMessages?.includes(response.message)) {
      return { ok: true, response, is_duplicate: true };
    }
    return { ok: false, response };
  }
}

/**
 * ฟังก์ชัน retry กลาง แทนที่ callAgain* ที่เคยก็อปเกือบเหมือนกันทุกตัว
 * ไม่ยัด setState เข้ามาตรงๆ แต่รับเป็น callback ให้แต่ละไฟล์ต่อเข้ากับ state ของตัวเอง
 * (local state / redux slice)
 *
 * @param {object} params
 * @param {string[]} params.keys - stateBtnAgain (request.key ที่เลือกไว้)
 * @param {Array<{request: object, response: object, retryType?: 'ca', account_id?: string, oneIdResponse?: object}>} params.memberOneFail
 * @param {string[]} params.objective
 * @param {string[]} [params.objectiveCheck] - ใช้ตัดสิน RA ในหน้า add (หน้า history มี 'one-id-ra' ใน objective แล้ว)
 * @param {string} params.business_id
 * @param {string} [params.file_id]
 * @param {string[]} [params.services]
 * @param {number} [params.delay=500] - ดีเลย์ระหว่างรายการ (ไม่รอหลังรายการสุดท้าย)
 * @param {(key: string) => void} [params.onItemStart]
 * @param {(key: string, result: {request, response, is_duplicate?}) => void} params.onItemSuccess
 * @param {(key: string, result: {request, response}) => void} params.onItemFail
 * @returns {Promise<{ successKeys: string[], failKeys: string[] }>}
 */
export async function retryPreRegisterItems({
  keys,
  memberOneFail,
  objective,
  objectiveCheck = [],
  business_id,
  file_id,
  services,
  delay = 500,
  onItemStart,
  onItemSuccess,
  onItemFail,
}) {
  const successKeys = [];
  const failKeys = [];
  const ctx = { business_id, objective, objectiveCheck, services, file_id };

  // reverse() เหมือนโค้ดเดิม (ลำดับการ retry จากรายการล่างขึ้นบน) — copy ก่อนเพื่อไม่ mutate state
  const ordered = [...keys].reverse();
  for (const [i, key] of ordered.entries()) {
    const match = memberOneFail.find((member) => member.request.key === key);
    if (!match) continue;

    onItemStart?.(key);

    let ok;
    let result;
    if (match.retryType === 'ca') {
      try {
        const { data } = await axios({
          method: 'POST',
          url: `${process.env.REACT_APP_BACKEND_URL}/api/v5/ca`,
          data: buildCAPayload({
            request: match.request,
            response: { data: { account_id: match.account_id } },
          }, business_id),
        });
        ok = true;
        result = {
          request: match.request,
          response: match.oneIdResponse,
          caResponse: data,
          caRetry: true,
        };
      } catch (error) {
        ok = false;
        const caError = error.response?.data;
        result = {
          ...match,
          response: buildCAFailResponse(error),
        };
      }
    } else {
      const { response, is_duplicate, ok: submitted } = await submitPreRegisterItem(match.request, ctx);
      ok = submitted;
      result = { request: match.request, response, ...(is_duplicate && { is_duplicate }) };
    }

    if (ok) {
      successKeys.push(key);
      onItemSuccess?.(key, result);
    } else {
      failKeys.push(key);
      onItemFail?.(key, result);
    }

    if (i < ordered.length - 1) await sleep(delay);
  }

  return { successKeys, failKeys };
}